import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { addMonths } from "date-fns";
import type React from "react";
import { useState } from "react";

import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import type { DateRangePickerValue } from "@/components/DateRangePicker/DateRangePicker";
import { DateRangePicker } from "@/components/DateRangePicker/DateRangePicker";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { NoData } from "@/components/NoData/NoData";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { MIN_ANALYTICS_DATE } from "@/helpers/analytics";
import { parseQueryParamToDate } from "@/helpers/query-params";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";

import { FahHistoryQuestionGroupCard } from "./components/FahHistoryQuestionGroupCard";
import { FeelingAtHomeExportModal } from "./components/FeelAtHomeExportModal";

export function FeelingAtHomeDetailsPage(): React.ReactNode {
  const clockDate = useCurrentDate();
  const { t } = useTranslation();
  const sessionUser = useSessionUser();

  const [queryParamMonth, setQueryParamMonth] = useQueryParam("month");
  const [dateRange, setDateRange] = useState<DateRangePickerValue | undefined>(() => {
    if (!queryParamMonth) {
      return undefined;
    }

    const date = parseQueryParamToDate(queryParamMonth + "-01");
    if (!date) {
      return undefined;
    }

    return [date, addMonths(date, 1)];
  });
  const [isExportModalOpened, showSurveyExportModalHandlers] = useBool(false);
  const startDate = dateRange?.[0];
  const endDate = dateRange?.[1];
  const startDateIso = startDate?.toISOString();
  const endDateIso = endDate?.toISOString();

  function handleChangeDateRange(range: DateRangePickerValue | undefined) {
    setDateRange(range);

    if (!range && queryParamMonth) {
      setQueryParamMonth(null, { replace: true });
    }
  }

  const analyticsQueries = useAnalyticsQueries();
  const {
    data: fahHistoryDetails,
    isPending: isPendingFahHistoryDetails,
    isPlaceholderData,
    error: errorFahHistoryDetails,
  } = useQuery({
    ...analyticsQueries.fahHistoryDetails({
      startDate: startDateIso,
      endDate: endDateIso,
    }),
    enabled: sessionUser.project.type !== "companyBased",
    placeholderData: keepPreviousData,
  });

  if (sessionUser.project.type === "companyBased") {
    return <ErrorPage status={404} />;
  }

  if (isPendingFahHistoryDetails) {
    return <FullSizeLoader withPadding />;
  }

  if (errorFahHistoryDetails) {
    return <ErrorPage error={errorFahHistoryDetails} />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.fah-history.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: sessionUser.project.name,
            },
            {
              name: t("page.fah-history.title"),
            },
          ]}
        />
      }
      actions={
        <>
          <AskNobiButton
            defaultFilters={{ projectIds: [sessionUser.project.id] }}
            context={{
              scope: "project",
              entity: "feeling-at-home",
              page: "detail",
              filters: {
                startDate: startDateIso,
                endDate: endDateIso,
              },
            }}
            suggestions={[
              t("component.ask-nobi-chat-drawer.feeling-at-home.suggestion.1"),
              t("component.ask-nobi-chat-drawer.feeling-at-home.suggestion.2"),
            ]}
          />
          <Button
            variant="soft"
            icon={<Download01Icon />}
            onClick={showSurveyExportModalHandlers.setTrue}
            disabled={isPlaceholderData}
          >
            {t("page.feeling-at-home-details.export.button")}
          </Button>
        </>
      }
      header={
        <Inline gap="sm" align="center" wrap={false}>
          <div className="w-full @md:max-w-search-standard">
            <DateRangePicker
              value={dateRange}
              onChange={handleChangeDateRange}
              placeholder={[t("common.label.start-date"), t("common.label.end-date")]}
              min={MIN_ANALYTICS_DATE}
              max={clockDate}
            />
          </div>
        </Inline>
      }
    >
      <Stack gap="sm">
        <span className="text-caption">
          {fahHistoryDetails.totalResponseCount
            ? t("common.entity.response", {
                count: fahHistoryDetails.totalResponseCount,
              })
            : t("common.label.not-applicable")}
        </span>
        <PageGrid.Row>
          {fahHistoryDetails.questions.length > 0 ? (
            <>
              {fahHistoryDetails.questions.map((question, index) => (
                <PageGrid.Column key={question.id} size="50%">
                  <FahHistoryQuestionGroupCard order={index + 1} question={question} isLoading={isPlaceholderData} />
                </PageGrid.Column>
              ))}
            </>
          ) : (
            <PageGrid.Column size="50%">
              <SectionCard>
                <NoData horizontal />
              </SectionCard>
            </PageGrid.Column>
          )}
        </PageGrid.Row>
      </Stack>

      <FeelingAtHomeExportModal
        isOpened={isExportModalOpened}
        startDate={startDate}
        endDate={endDate}
        onOpenChange={showSurveyExportModalHandlers.set}
      />
    </DocumentPage>
  );
}
